import React from 'react';
import { Message } from '../useExercisePractice';
import { hebrewLetters } from '../../../configs/config';
import { useQuestionsStore } from '../../../utils/questionStore';
import { quizClueIcon, captureExerciseIcon } from '../../../assets/images';

import { isMobileDevice } from '../../../utils/utils';

export interface ResumeParams {
  selectedQuestionId: number | null;
  selectedQuestionLetter: string | null;
  activeQuestionSteps: any[];
  activeStepIndex: number;
  allStepsCompleted: boolean;
  exerciseQuestions: any[];
  exerciseId: number | null;
  messages: Message[];
  t: (key: string, options?: any) => string;
  selectQuestionFromBubble: (q: any, letter: string) => void;
  loadQuestionSteps: (retry?: boolean, targetQId?: number, targetQ?: any) => void;
  handleShowStepSolution: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleStepTutoring: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleStepSolved: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  showInfoToast: (msg: string) => void;
  handleCaptureStart?: () => void;
}

/**
 * Parses message history and state parameters to resolve the context-appropriate follow-up option buttons.
 */
export const getCurrentStepOptions = (params: ResumeParams) => {
  const {
    selectedQuestionId,
    selectedQuestionLetter,
    activeQuestionSteps,
    activeStepIndex,
    allStepsCompleted,
    exerciseQuestions,
    exerciseId,
    messages,
    t,
    selectQuestionFromBubble,
    loadQuestionSteps,
    handleShowStepSolution,
    handleStepTutoring,
    handleStepSolved,
    showInfoToast,
    handleCaptureStart
  } = params;

  if (!selectedQuestionId) {
    return [];
  }

  if (activeQuestionSteps.length === 0) {
    return [];
  }

  const qId = selectedQuestionId;
  const qLetter = selectedQuestionLetter || 'א';

  if (allStepsCompleted) {
    if (!exerciseId) return [];
    const history = useQuestionsStore.getState().getQuestionHistory(exerciseId);
    const completed = history?.completed || [];
    const nextQ = exerciseQuestions.find((q: any) => !completed.includes(q.id));
    const nextQIndex = exerciseQuestions.findIndex((q: any) => !completed.includes(q.id));
    const nextLetter = nextQIndex !== -1 ? hebrewLetters[nextQIndex] : 'א';

    if (nextQ) {
      return [
        {
          label: t("exercisePracticeContinueNextQuestion"),
          value: "next_question",
          action: () => {
            selectQuestionFromBubble(nextQ, nextLetter);
            loadQuestionSteps(false, nextQ.id, nextQ);
          }
        }
      ];
    }
    return [];
  }

  const stepIndex = activeStepIndex;
  const step = activeQuestionSteps[stepIndex];
  if (!step) return [];

  const stepContentMsgId = `step-content-bubble-${step.id}-${stepIndex}`;
  const solutionLoaderIdPrefix = `solver-loader-`;
  const tutorLoaderIdPrefix = `tutor-loader-`;
  const explanationMsgId = `deep-explanation-${step.id}`;

  // Find the last message related to this step in history
  const reversedMessages = [...messages].reverse();
  const lastMsg = reversedMessages.find(m => m.id?.includes(step.id) || m.id === stepContentMsgId);

  if (lastMsg) {
    if (lastMsg.id?.startsWith(`deep-explanation-`) || lastMsg.id === explanationMsgId) {
      return [
        {
          label: t("understoodNextStep"),
          value: "next_step",
          action: () => handleStepSolved(stepIndex, activeQuestionSteps, qId, qLetter)
        }
      ];
    }

    const isSolution = lastMsg.id?.startsWith(solutionLoaderIdPrefix) || lastMsg.id?.startsWith(`solution-followup-`);
    if (isSolution) {
      return [
        {
          label: t("understoodNextStep"),
          value: "understood_next_step",
          action: () => handleStepSolved(stepIndex, activeQuestionSteps, qId, qLetter)
        }
      ];
    }

  }

  // Default fallback: notebook solving prompt options
  return getNotebookPromptOptions(
    t,
    () => handleShowStepSolution(stepIndex, activeQuestionSteps, qId, qLetter),
    () => handleStepTutoring(stepIndex, activeQuestionSteps, qId, qLetter),
    showInfoToast,
    handleCaptureStart,
    () => handleStepSolved(stepIndex, activeQuestionSteps, qId, qLetter)
  );
};

export const getNotebookPromptOptions = (
  t: (key: string) => string,
  handleShowStepSolution: () => void,
  handleStepTutoring: () => void,
  showInfoToast: (msg: string) => void,
  handleCaptureStart?: () => void,
  handleStepSolved?: () => void
) => [
  {
    label: t("exercisePracticeSolutionBtn"),
    value: "correct",
    action: handleShowStepSolution,
    color: "black" as const,
    variant: "contained" as const
  },
  {
    label: t("exercisePracticeClueBtn"),
    value: "not_yet",
    action: handleStepTutoring,
    color: "black" as const,
    variant: "contained" as const,
    icon: React.createElement('img', { src: quizClueIcon, alt: 'clue' })
  },
  {
    label: isMobileDevice() ? t("exercisePracticeCaptureSolutionBtn") : t("exercisePracticeUploadSolutionBtn"),
    value: "capture",
    action: handleCaptureStart || (() => showInfoToast(t("stillUnderDevelopment"))),
    color: "black" as const,
    variant: "contained" as const,
    icon: React.createElement('img', { src: captureExerciseIcon, alt: 'capture' })
  },
  {
    label: t("exercisePracticeSkipToNextStep"),
    value: "skip_step",
    action: handleStepSolved || (() => {}),
    color: "dark" as const,
    variant: "outlined" as const
  }
];
